import { Component, OnInit } from '@angular/core';

export interface AppFormData {
  // Step 1 - Project Details
  appType: string;
  complexity: string;
  timeline: string;
  budget: string;
  
  // Step 2 - Project Description
  projectName: string;
  description: string;
  
  // Step 3 - Contact Information
  name: string;
  email: string;
  company: string;
  countryCode: string;
  phone: string;
  acceptTerms: boolean;
}

@Component({
  selector: 'appweb',
  templateUrl: './appweb.component.html',
  styleUrls: ['./appweb.component.scss']
})
export class appwebComponent implements OnInit {
  
  // Form step management
  currentStep: number = 1;
  totalSteps: number = 3;
  
  // Form data
  formData: AppFormData = {
    appType: '',
    complexity: '',
    timeline: '',
    budget: '',
    projectName: '',
    description: '',
    name: '',
    email: '',
    company: '',
    countryCode: '+33',
    phone: '',
    acceptTerms: false
  };
  
  // Form state
  isSubmitting: boolean = false;
  submissionSuccess: boolean = false;
  submissionError: string = '';
  
  // App type options
  appTypeOptions = [
    { value: 'mobile-ios', label: 'Application Mobile iOS' },
    { value: 'mobile-android', label: 'Application Mobile Android' },
    { value: 'mobile-hybride', label: 'Application Mobile Hybride' },
    { value: 'web-app', label: 'Application Web' },
    { value: 'ecommerce', label: 'E-commerce Mobile' },
    { value: 'autre', label: 'Autre' }
  ];
  
  // Complexity options
  complexityOptions = [
    { value: 'simple', label: 'Simple (5-10 écrans)' },
    { value: 'moyenne', label: 'Moyenne (10-20 écrans)' },
    { value: 'complexe', label: 'Complexe (20+ écrans)' },
    { value: 'enterprise', label: 'Enterprise' }
  ];
  
  // Timeline options
  timelineOptions = [
    { value: '1-2-mois', label: '1-2 mois' },
    { value: '3-4-mois', label: '3-4 mois' },
    { value: '6-mois', label: '6 mois ou plus' },
    { value: 'flexible', label: 'Flexible' }
  ];
  
  // Budget options
  budgetOptions = [
    { value: '15000-30000', label: '15 000 - 30 000 MAD' },
    { value: '30000-60000', label: '30 000 - 60 000 MAD' },
    { value: '60000-100000', label: '60 000 - 100 000 MAD' },
    { value: '100000+', label: '100 000+ MAD' }
  ];

  constructor() { }

  ngOnInit(): void {
    // Initialize component
    this.updateSteps();
  }

  // Step navigation methods
  nextStep(): void {
    if (this.currentStep < this.totalSteps && this.isCurrentStepValid()) {
      this.currentStep++;
      this.updateSteps();
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateSteps();
    }
  }

  updateSteps(): void {
    // This method can be used to trigger any step-specific logic
    // The actual visual updates are handled by the template
  }

  // Form validation methods
  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.isStep1Valid();
      case 2:
        return this.isStep2Valid();
      case 3:
        return this.isStep3Valid();
      default:
        return false;
    }
  }

  isStep1Valid(): boolean {
    return !!(this.formData.appType);
  }

  isStep2Valid(): boolean {
    return !!(this.formData.projectName && this.formData.description);
  }

  isStep3Valid(): boolean {
    return !!(
      this.formData.name && 
      this.formData.email && 
      this.isValidEmail(this.formData.email) &&
      this.formData.phone &&
      this.formData.acceptTerms
    );
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Form submission
  async onSubmit(): Promise<void> {
    if (!this.isStep3Valid()) {
      return;
    }

    this.isSubmitting = true;
    this.submissionError = '';

    try {
      // Submit form data via Web3Forms
      const result = await this.submitFormData(this.formData);
      
      this.submissionSuccess = true;
      
      // Track Google Ads conversion - ONLY on successful form submission
      this.trackGoogleConversion();
      
      // Show success message for 5 seconds before resetting
      setTimeout(() => {
        this.resetForm();
      }, 5000);
      
      // Optional: scroll to success message
      setTimeout(() => {
        const successElement = document.querySelector('.success-alert');
        if (successElement) {
          successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
    } catch (error: any) {
      console.error('Form submission error:', error);
      this.submissionError = error.message || 'Une erreur est survenue lors de l\'envoi du formulaire. Veuillez réessayer.';
    } finally {
      this.isSubmitting = false;
    }
  }

  // Submit form data to Web3Forms
  private async submitFormData(data: AppFormData): Promise<any> {
    return new Promise((resolve, reject) => {
      // Create the form data to send
      const formData = new FormData();
      
      // Add your Web3Forms access key (same as rhillane component that works)
      formData.append('access_key', 'ed3bd6d3-e198-4b84-b8cf-7b59f9ea2aba'); 
      
      // Add basic form fields
      formData.append('name', data.name || '');
      formData.append('email', data.email || '');
      formData.append('company', data.company || 'Non spécifié');
      formData.append('phone', data.countryCode && data.phone ? `${data.countryCode} ${data.phone}` : (data.phone || 'Non spécifié'));
      formData.append('appType', data.appType || 'Non spécifié');
      formData.append('complexity', data.complexity || 'Non spécifié');
      formData.append('timeline', data.timeline || 'Non spécifié');
      formData.append('budget', data.budget || 'Non spécifié');
      formData.append('projectName', data.projectName || 'Non spécifié');
      formData.append('description', data.description || '');
      
      // Create a comprehensive message
      const comprehensiveMessage = `
NOUVELLE DEMANDE DE DÉVELOPPEMENT D'APPLICATION

=== INFORMATIONS CLIENT ===
Nom: ${data.name}
Email: ${data.email}
Entreprise: ${data.company || 'Non spécifié'}
Téléphone: ${data.countryCode && data.phone ? `${data.countryCode} ${data.phone}` : (data.phone || 'Non spécifié')}

=== DÉTAILS DU PROJET ===
Nom du projet: ${data.projectName || 'Non spécifié'}
Type d'application: ${this.getAppTypeLabel(data.appType)}
Complexité: ${this.getComplexityLabel(data.complexity)}
Délai souhaité: ${this.getTimelineLabel(data.timeline)}
Budget: ${this.getBudgetLabel(data.budget)}

=== DESCRIPTION DU PROJET ===
${data.description || 'Aucune description fournie'}

=== INFORMATIONS SUPPLÉMENTAIRES ===
Conditions acceptées: ${data.acceptTerms ? 'Oui' : 'Non'}
Date de soumission: ${new Date().toLocaleString('fr-FR')}
      `.trim();
      
      formData.append('message', comprehensiveMessage);
      
      // Optional: add a custom subject line
      formData.append('subject', `Demande développement app: ${data.projectName || 'Sans nom'} - ${data.name}`);
      
      // Send the form using Fetch API
      fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erreur réseau');
        }
        return response.json();
      })
      .then(responseData => {
        if (responseData.success) {
          resolve({ 
            success: true, 
            message: 'Demande de développement envoyée avec succès!' 
          });
        } else {
          reject(new Error('Erreur lors de l\'envoi de la demande'));
        }
      })
      .catch(error => {
        console.error('Erreur:', error);
        reject(error);
      });
    });
  }

  // Helper methods to get labels from values
  private getAppTypeLabel(value: string): string {
    const option = this.appTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value || 'Non spécifié';
  }

  private getComplexityLabel(value: string): string {
    const option = this.complexityOptions.find(opt => opt.value === value);
    return option ? option.label : value || 'Non spécifié';
  }

  private getTimelineLabel(value: string): string {
    const option = this.timelineOptions.find(opt => opt.value === value);
    return option ? option.label : value || 'Non spécifié';
  }

  private getBudgetLabel(value: string): string {
    const option = this.budgetOptions.find(opt => opt.value === value);
    return option ? option.label : value || 'Non spécifié';
  }

  // Reset form
  resetForm(): void {
    this.formData = {
      appType: '',
      complexity: '',
      timeline: '',
      budget: '',
      projectName: '',
      description: '',
      name: '',
      email: '',
      company: '',
      countryCode: '+33',
      phone: '',
      acceptTerms: false
    };
    this.currentStep = 1;
    this.submissionSuccess = false;
    this.submissionError = '';
    this.updateSteps();
  }

  // Track Google Ads conversion - called ONLY on successful form submission
  private trackGoogleConversion(): void {
    try {
      // Check if gtag is available (Google Ads/Analytics)
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL', // Replace with your actual conversion ID
          'value': 1.0,
          'currency': 'EUR',
          'transaction_id': ''
        });
      }
      
      // Alternative: If using Google Tag Manager dataLayer
      if (typeof (window as any).dataLayer !== 'undefined') {
        (window as any).dataLayer.push({
          'event': 'form_submission',
          'formType': 'application_web',
          'formName': 'Demande développement application',
          'value': this.formData.budget || 'unknown'
        });
      }
      
      console.log('Google conversion tracked for app form submission');
    } catch (error) {
      console.error('Error tracking Google conversion:', error);
    }
  }

  // CTA button handlers
  scrollToForm(): void {
    const formElement = document.querySelector('.form-wrapper');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  // Get current year for footer
  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  // Platform ratings data
  getPlatformRatings() {
    return [
      {
        name: 'Google',
        rating: 4.9,
        logo: 'google',
        color: '#4285F4'
      },
      {
        name: 'Provenexpert',
        rating: 4.5,
        logo: 'provenexpert',
        color: '#f0d000'
      },
      {
        name: 'Clutch',
        rating: 4.6,
        logo: 'clutch',
        color: '#ff6347'
      },
      {
        name: 'Sortlist',
        rating: 4.8,
        logo: 'sortlist',
        color: '#000000'
      }
    ];
  }

  // Technology stack data
  getTechStack() {
    return {
      mobile: [
        { name: 'iOS Native', tech: 'Swift', color: '#007AFF' },
        { name: 'Kotlin', tech: 'Kt', color: '#3DDC84' },
        { name: 'React Native', tech: 'RN', color: '#61DAFB' },
        { name: 'Flutter', tech: 'FL', color: '#02569B' }
      ],
      web: [
        { name: 'React.js', tech: 'React', color: '#61DAFB' },
        { name: 'Angular', tech: 'Ng', color: '#DD0031' },
        { name: 'Vue.js', tech: 'Vue', color: '#4FC08D' },
        { name: 'Next.js', tech: 'Next', color: '#000000' }
      ],
      backend: [
        { name: 'Java', tech: 'Java', color: '#c74634' },
        { name: 'Python', tech: 'Py', color: '#3776AB' },
        { name: 'PHP/Laravel', tech: 'Laravel', color: '#F05032' },
        { name: '.NET Core', tech: '.NET', color: '#239120' }
      ],
      database: [
        { name: 'PostgreSQL', tech: 'SQL', color: '#336791' },
        { name: 'MySQL', tech: 'MySQL', color: '#4479A1' },
        { name: 'MongoDB', tech: 'Mongo', color: '#47A248' },
        { name: 'Redis', tech: 'Redis', color: '#DC382D' }
      ]
    };
  }

  // Development process data
  getDevelopmentProcess() {
    return [
      {
        number: '01',
        icon: '🎯',
        title: 'Analyse & Planification',
        description: 'Étude approfondie de vos besoins, définition des fonctionnalités et création du cahier des charges détaillé.',
        duration: '1-2 semaines'
      },
      {
        number: '02',
        icon: '🎨',
        title: 'Design & Prototypage',
        description: 'Création des maquettes UI/UX, prototypes interactifs et validation du design avec votre équipe.',
        duration: '2-3 semaines'
      },
      {
        number: '03',
        icon: '⚡',
        title: 'Développement',
        description: 'Développement agile avec livraisons régulières, tests continus et intégration des retours clients.',
        duration: '4-8 semaines'
      },
      {
        number: '04',
        icon: '🧪',
        title: 'Tests & Optimisation',
        description: 'Tests exhaustifs, optimisation des performances et correction des bugs avant la mise en production.',
        duration: '1-2 semaines'
      },
      {
        number: '05',
        icon: '🚀',
        title: 'Déploiement & Lancement',
        description: 'Mise en production, configuration des environnements et accompagnement lors du lancement.',
        duration: '1 semaine'
      },
      {
        number: '06',
        icon: '🛠️',
        title: 'Support & Maintenance',
        description: 'Support technique continu, mises à jour régulières et évolutions fonctionnelles selon vos besoins.',
        duration: 'En continu'
      }
    ];
  }
  navigateToQuickLink(quickLink: string): void {
    // Handle navigation to quick link pages
    console.log(`Navigating to: ${quickLink}`);
    // Implement your routing logic here
  }
  navigateToExploration(explorationLink: string): void {
    // Handle navigation to recent exploration pages
    console.log(`Navigating to: ${explorationLink}`);
    // Implement your routing logic here
  }
  navigateToService(serviceLink: string): void {
    // Handle navigation to service pages
    console.log(`Navigating to: ${serviceLink}`);
    // Implement your routing logic here
  }
  currentYear = new Date().getFullYear();

  // Pricing plans data
  getPricingPlans() {
    return [
      {
        name: 'APP MOBILE',
        subtitle: 'Application mobile simple et efficace',
        badge: '📱 iOS ou Android',
        oldPrice: '25 000 MAD',
        currentPrice: '15 000 MAD',
        features: [
          '5-8 écrans principaux',
          'Design UI/UX moderne',
          'Intégration API basique',
          'Tests et optimisation',
          'Publication sur Store',
          'Support 30 jours'
        ],
        buttonClass: 'starter-button',
        isPopular: false
      },
      {
        name: 'CROSS-PLATFORM',
        subtitle: 'Application iOS et Android en une seule fois',
        badge: '🚀 iOS + Android',
        oldPrice: '45 000 MAD',
        currentPrice: '28 000 MAD',
        features: [
          '10-15 écrans avancés',
          'Design personnalisé',
          'Fonctionnalités avancées',
          'Intégrations multiples',
          'Backend inclus',
          'Support 60 jours'
        ],
        buttonClass: 'pro-button',
        isPopular: true
      },
      {
        name: 'ENTERPRISE',
        subtitle: 'Solution complète sur mesure',
        badge: '🏢 Solution complète',
        oldPrice: '80 000 MAD',
        currentPrice: '50 000 MAD',
        features: [
          'Application complexe illimitée',
          'Panel d\'administration',
          'API personnalisées',
          'Intégrations tierces',
          'Formation équipe',
          'Support 6 mois'
        ],
        buttonClass: 'flex-button',
        isPopular: false
      }
    ];
  }
}